export interface ChartPoint {
    x: number
    y: number
}

interface CubicSegment {
    from: ChartPoint
    c1: ChartPoint
    c2: ChartPoint
    to: ChartPoint
}

const round = (value: number): number => Math.round(value * 100) / 100

const point = (value: ChartPoint): string => `${round(value.x)},${round(value.y)}`

const niceStep = (rough: number): number => {
    if (rough <= 0) return 1

    const exponent = Math.floor(Math.log10(rough))
    const fraction = rough / 10 ** exponent

    let nice = 10

    if (fraction <= 1) nice = 1
    else if (fraction <= 2) nice = 2
    else if (fraction <= 2.5) nice = 2.5
    else if (fraction <= 5) nice = 5

    return nice * 10 ** exponent
}

export const niceTicks = (min: number, max: number, count = 4): number[] => {
    const step = niceStep((max - min) / count)
    const start = Math.floor(min / step) * step
    const end = Math.ceil(max / step) * step
    const ticks: number[] = []

    for (let value = start; value <= end + step / 2; value += step) {
        ticks.push(Math.round(value * 100) / 100)
    }

    return ticks
}

// Monotone cubic interpolation (Fritsch-Carlson), so curves never overshoot the data.
const monotoneSegments = (points: ChartPoint[]): CubicSegment[] => {
    const count = points.length

    if (count < 2) return []

    const dx: number[] = []
    const slopes: number[] = []

    for (let index = 0; index < count - 1; index++) {
        dx[index] = points[index + 1].x - points[index].x
        slopes[index] = dx[index] === 0 ? 0 : (points[index + 1].y - points[index].y) / dx[index]
    }

    const tangents: number[] = new Array(count).fill(0)

    tangents[0] = slopes[0]
    tangents[count - 1] = slopes[count - 2]

    for (let index = 1; index < count - 1; index++) {
        const before = slopes[index - 1]
        const after = slopes[index]

        if (before * after <= 0) {
            tangents[index] = 0
        }
        else {
            const weightA = 2 * dx[index] + dx[index - 1]
            const weightB = dx[index] + 2 * dx[index - 1]

            tangents[index] = (weightA + weightB) / (weightA / before + weightB / after)
        }
    }

    const segments: CubicSegment[] = []

    for (let index = 0; index < count - 1; index++) {
        const third = dx[index] / 3
        const from = points[index]
        const to = points[index + 1]

        segments.push({
            from,
            c1: { x: from.x + third, y: from.y + third * tangents[index] },
            c2: { x: to.x - third, y: to.y - third * tangents[index + 1] },
            to
        })
    }

    return segments
}

export const linePath = (points: ChartPoint[]): string => {
    if (points.length === 0) return ''
    if (points.length === 1) return `M${point(points[0])}`

    const segments = monotoneSegments(points)

    return segments.reduce(
        (path, segment) => `${path}C${point(segment.c1)} ${point(segment.c2)} ${point(segment.to)}`,
        `M${point(points[0])}`
    )
}

export const areaToBaselinePath = (points: ChartPoint[], baseline: number): string => {
    if (points.length < 2) return ''

    const first = points[0]
    const last = points[points.length - 1]

    return `${linePath(points)}L${round(last.x)},${round(baseline)}L${round(first.x)},${round(baseline)}Z`
}

export const bandPath = (upper: ChartPoint[], lower: ChartPoint[]): string => {
    if (upper.length < 2 || lower.length < 2) return ''

    const reversed = monotoneSegments(lower).reverse()
    const lowerLast = lower[lower.length - 1]

    const back = reversed.reduce(
        (path, segment) => `${path}C${point(segment.c2)} ${point(segment.c1)} ${point(segment.from)}`,
        `L${point(lowerLast)}`
    )

    return `${linePath(upper)}${back}Z`
}
